import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import api from '../api/axios';
import UserNavbar from '../components/UserNavbar';
import { toast } from 'react-toastify';

const PROCESSING_FEE = 5;

function formatSlotDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    weekday: 'long',
  });
}

function formatTime(timeStr) {
  if (!timeStr) return '—';
  const [h, m] = timeStr.split(':').map(Number);
  const d = new Date();
  d.setHours(h, m, 0, 0);
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
}

function getMediaUrl(path) {
  if (!path) return null;
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
  if (typeof path === 'string' && (path.startsWith('http://') || path.startsWith('https://'))) return path;
  if (typeof path === 'string' && path.startsWith('/media/')) return `${apiUrl}${path}`;
  return `${apiUrl}/media/${path}`;
}

function SectionBlock({ icon, title, onEdit, editLabel = 'Edit', children, className = '' }) {
  return (
    <div className={className}>
      <div className="d-flex justify-content-between align-items-center mb-2 gap-2">
        <div className="d-flex align-items-center gap-2 min-w-0">
          <div
            className="d-flex align-items-center justify-content-center rounded-2 flex-shrink-0"
            style={{ width: 28, height: 28, background: '#EBF3FF', color: '#2563EB', fontSize: 13 }}
          >
            <i className={`bi ${icon}`} />
          </div>
          <h6 className="fw-bold mb-0 text-dark text-truncate" style={{ fontSize: 14 }}>{title}</h6>
        </div>
        {onEdit && (
          <button
            type="button"
            className="btn btn-link text-primary text-decoration-none p-0 fw-semibold small flex-shrink-0"
            onClick={onEdit}
            style={{ fontSize: 12 }}
          >
            <i className={`bi ${editLabel === 'Done' ? 'bi-check-lg' : 'bi-pencil'} me-1`} />
            {editLabel}
          </button>
        )}
      </div>
      {children}
    </div>
  );
}

function FileCard({ name, subtitle, onView }) {
  return (
    <div
      className="d-flex align-items-center gap-2 px-2 py-2 rounded-3 border flex-grow-1"
      style={{ background: '#F8FAFC', borderColor: '#E5E7EB' }}
    >
      <div
        className="d-flex align-items-center justify-content-center rounded-2 flex-shrink-0"
        style={{ width: 36, height: 36, background: '#FEE2E2', color: '#DC2626' }}
      >
        <i className="bi bi-file-earmark-pdf-fill" />
      </div>
      <div className="overflow-hidden flex-grow-1">
        <p className="fw-semibold mb-0 text-truncate" style={{ fontSize: 13 }}>{name}</p>
        <small className="text-muted" style={{ fontSize: 11 }}>{subtitle}</small>
      </div>
      {onView && (
        <button
          type="button"
          className="btn btn-link text-primary text-decoration-none p-0 fw-semibold small flex-shrink-0"
          onClick={onView}
          style={{ fontSize: 12 }}
        >
          View
        </button>
      )}
    </div>
  );
}

function paymentStatusBadge(status) {
  const map = {
    SUCCEEDED: 'bg-success',
    REFUNDED: 'bg-info text-dark',
    FAILED: 'bg-danger',
    PENDING: 'bg-warning text-dark',
    PROCESSING: 'bg-info text-dark',
    CANCELED: 'bg-secondary',
    'Pending Payment': 'bg-warning text-dark',
  };
  return map[status] || 'bg-secondary';
}

function BookingSummary() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const slotId = searchParams.get('slot_id');
  const familyMemberId = searchParams.get('family_member_id');
  const appointmentIdParam = searchParams.get('appointment_id');
  const paymentIdParam = searchParams.get('payment_id');

  const isHistoryView = !!(appointmentIdParam || location.state?.fromPaymentHistory);
  const historyPayment = location.state?.payment || null;

  const backTo = location.state?.backTo || null;
  const stateFamilyMember = location.state?.familyMember || null;

  const [loading, setLoading] = useState(true);
  const [slot, setSlot] = useState(null);
  const [doctor, setDoctor] = useState(null);
  const [user, setUser] = useState(null);
  const [familyMember, setFamilyMember] = useState(stateFamilyMember);
  const [booking, setBooking] = useState(null);
  const [paymentInfo, setPaymentInfo] = useState(historyPayment);
  const [reasonNotes, setReasonNotes] = useState(location.state?.patientNotes || '');
  const [medicalReportFiles, setMedicalReportFiles] = useState(() => {
    if (Array.isArray(location.state?.medicalReportFiles) && location.state.medicalReportFiles.length) {
      return location.state.medicalReportFiles;
    }
    if (location.state?.medicalReportFile) return [location.state.medicalReportFile];
    return [];
  });
  const [editingReason, setEditingReason] = useState(false);
  const [editingReport, setEditingReport] = useState(false);
  const reportInputRef = useRef(null);

  useEffect(() => {
    const load = async () => {
      try {
        const sessionRes = await api.get('user/session-check/');
        const userId = sessionRes.data?.user_id;
        if (userId) {
          const profileRes = await api.get(`user/profile/${userId}/`);
          setUser(profileRes.data);
        }

        // History view: load booking + payment by appointment_id
        if (appointmentIdParam) {
          const bookingsRes = await api.get('appointments/user/my-bookings/');
          const list = Array.isArray(bookingsRes.data) ? bookingsRes.data : [];
          const found = list.find(
            (b) => String(b.appointment_id) === String(appointmentIdParam)
          );
          if (!found) {
            toast.error('Booking not found');
            setLoading(false);
            return;
          }
          setBooking(found);
          setSlot({
            date: found.date,
            start_time: found.start_time,
            end_time: found.end_time,
            call_type: found.call_type || 'VIDEO',
            duration: null,
            doctor_id: found.doctor_id,
          });
          if (found.doctor_id) {
            try {
              const docRes = await api.get(`doctor/profile/${found.doctor_id}/`);
              setDoctor(docRes.data);
            } catch {
              setDoctor({
                id: found.doctor_id,
                name: found.doctor_name,
                specialization: found.doctor_specialization,
              });
            }
          }
          if (found.family_member_id) {
            setFamilyMember({
              id: found.family_member_id,
              name: found.family_member_name || found.patient_name,
              relation: found.family_member_relation,
            });
          }

          let pay = historyPayment;
          if (!pay || String(pay.payment_id) !== String(paymentIdParam || pay.payment_id)) {
            try {
              const payRes = await api.get('payments/history/');
              const pays = Array.isArray(payRes.data) ? payRes.data : [];
              pay =
                pays.find((p) => String(p.payment_id) === String(paymentIdParam)) ||
                pays.find((p) => String(p.appointment_id) === String(appointmentIdParam)) ||
                null;
            } catch {
              /* keep state payment */
            }
          }
          setPaymentInfo(pay);
          setLoading(false);
          return;
        }

        // Pre-payment flow: needs slot_id
        if (!slotId) {
          setLoading(false);
          return;
        }

        const slotRes = await api.get(`appointments/user/slot/${slotId}/`);
        const slotData = slotRes.data;
        setSlot(slotData);

        const doctorId = slotData.doctor_id || slotData.doctor;
        if (doctorId) {
          const docRes = await api.get(`doctor/profile/${doctorId}/`);
          setDoctor(docRes.data);
        }

        if (familyMemberId) {
          try {
            const famRes = await api.get('user/family/');
            const famList = Array.isArray(famRes.data) ? famRes.data : (famRes.data?.results || []);
            const fm = famList.find((m) => String(m.id) === String(familyMemberId));
            if (fm) setFamilyMember(fm);
            else if (stateFamilyMember) setFamilyMember(stateFamilyMember);
          } catch {
            if (stateFamilyMember) setFamilyMember(stateFamilyMember);
          }
        }
      } catch (err) {
        console.error(err);
        toast.error(err.response?.data?.error || 'Failed to load booking summary');
      } finally {
        setLoading(false);
      }
    };

    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slotId, familyMemberId, appointmentIdParam, paymentIdParam]);

  const consultationFee = (() => {
    if (isHistoryView && paymentInfo) return Number(paymentInfo.consultation_fee) || 0;
    if (!doctor || !slot) return 0;
    const callType = slot.call_type || 'VIDEO';
    const fee = callType === 'AUDIO'
      ? doctor.audio_consultation_fees
      : doctor.consultation_fees;
    return parseFloat(fee) || 0;
  })();

  const platformFee = isHistoryView && paymentInfo
    ? (Number(paymentInfo.processing_fee) || 0)
    : PROCESSING_FEE;
  const totalAmount = isHistoryView && paymentInfo
    ? (Number(paymentInfo.total_amount) || consultationFee + platformFee)
    : consultationFee + platformFee;

  const paymentStatus = isHistoryView
    ? (paymentInfo?.status || 'PENDING')
    : 'Pending Payment';

  const displayBookingId = appointmentIdParam || booking?.appointment_id || null;
  const displayPaymentId = paymentIdParam || paymentInfo?.payment_id || null;
  const displayTransactionId = paymentInfo?.transaction_id || null;

  const callTypeLabel = (slot?.call_type || 'VIDEO') === 'AUDIO' ? 'Audio Call' : 'Video Call';
  const durationLabel = slot?.duration ? `${slot.duration} mins` : '';
  const patientLabel = familyMember
    ? `${familyMember.name}${familyMember.relation ? ` (${familyMember.relation})` : ''}`
    : booking?.family_member_name
      ? `${booking.family_member_name}${booking.family_member_relation ? ` (${booking.family_member_relation})` : ''}`
      : booking?.patient_name
        ? `${booking.patient_name} (Self)`
        : user?.name
          ? `${user.name} (Self)`
          : 'Self';

  const bookedByLabel = familyMember || booking?.family_member_name
    ? (user?.name || booking?.booked_by_name || 'Account holder')
    : null;

  const displayReason = isHistoryView
    ? (booking?.patient_notes || reasonNotes || '')
    : reasonNotes;

  const handleBack = () => {
    if (backTo) {
      navigate(backTo);
      return;
    }
    if (isHistoryView) {
      navigate('/user/payment-history');
      return;
    }
    if (doctor?.id || slot?.doctor_id) {
      navigate(`/user/book-appointment/${doctor?.id || slot.doctor_id}`);
      return;
    }
    navigate(-1);
  };

  const handleProceedToPayment = () => {
    if (!slotId) {
      toast.error('Slot is missing');
      return;
    }
    let url = `/user/payment?slot_id=${slotId}`;
    if (familyMemberId) url += `&family_member_id=${familyMemberId}`;
    navigate(url, {
      state: {
        patientNotes: (reasonNotes || '').trim(),
        medicalReportFiles,
        medicalReportFile: medicalReportFiles[0] || null,
        backTo: `${location.pathname}${location.search}`,
      },
    });
  };

  const MAX_REPORT_FILES = 5;

  const handleReportFileChange = (e) => {
    const incoming = Array.from(e.target.files || []);
    e.target.value = '';
    if (!incoming.length) return;
    const allowed = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    setMedicalReportFiles((prev) => {
      const next = [...prev];
      for (const file of incoming) {
        if (next.length >= MAX_REPORT_FILES) {
          toast.error(`You can upload up to ${MAX_REPORT_FILES} reports`);
          break;
        }
        if (!allowed.includes(file.type) && !/\.(pdf|jpe?g|png)$/i.test(file.name)) {
          toast.error(`${file.name}: Please upload a PDF or image (JPG/PNG)`);
          continue;
        }
        if (file.size > 10 * 1024 * 1024) {
          toast.error(`${file.name}: File size must be under 10MB`);
          continue;
        }
        next.push(file);
      }
      return next;
    });
  };

  const openReport = (file) => {
    if (!file) return;
    if (file instanceof File) {
      const url = URL.createObjectURL(file);
      window.open(url, '_blank');
      return;
    }
    const url = getMediaUrl(file);
    if (url) window.open(url, '_blank');
  };

  if (!slotId && !appointmentIdParam) {
    return (
      <div className="bg-light min-vh-100">
        <UserNavbar />
        <div className="container py-5">
          <div className="alert alert-danger">Booking or slot ID is required.</div>
          <button type="button" className="btn btn-primary" onClick={() => navigate('/user/dashboard')}>
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
        <div className="spinner-border text-primary" role="status" />
      </div>
    );
  }

  return (
    <div className="min-vh-100" style={{ background: '#F3F6FB' }}>
      <UserNavbar />

      <div className="container-fluid container-lg py-3 py-md-4 px-3 px-md-4" style={{ maxWidth: 1140 }}>
        <button
          type="button"
          className="btn btn-link text-decoration-none text-primary p-0 mb-3 fw-semibold"
          onClick={handleBack}
        >
          <i className="bi bi-arrow-left me-1" />
          {isHistoryView ? 'Back to Payment History' : 'Back to Booking'}
        </button>

        <div className="d-flex align-items-start align-items-md-center gap-3 mb-3 mb-md-4">
          <div
            className="d-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
            style={{ width: 48, height: 48, background: '#DBEAFE', color: '#2563EB' }}
          >
            <i className="bi bi-calendar2-check fs-4" />
          </div>
          <div className="min-w-0">
            <h3 className="fw-bold mb-0 text-dark" style={{ fontSize: 'clamp(1.25rem, 4vw, 1.75rem)' }}>
              Booking Summary
            </h3>
            <p className="text-muted mb-0 small">
              {isHistoryView
                ? 'Payment and booking details for this appointment.'
                : 'Please review your appointment details before proceeding to payment.'}
            </p>
          </div>
        </div>

        <div className="row g-3 g-lg-4">
          {/* Payment summary first on mobile for history/pay actions */}
          <div className="col-12 col-lg-4 order-1 order-lg-2">
            <div
              className="bg-white rounded-4 border p-3 p-md-4 booking-summary-sidebar"
              style={{ borderColor: '#E5E7EB' }}
            >
              <h6 className="fw-bold mb-3 d-flex align-items-center gap-2">
                <i className="bi bi-credit-card-2-front text-primary" />
                Payment Summary
              </h6>

              {isHistoryView && (displayBookingId || displayPaymentId || displayTransactionId) && (
                <div className="rounded-3 p-3 mb-3 border" style={{ background: '#F8FAFC', borderColor: '#E5E7EB' }}>
                  {displayBookingId && (
                    <div className="d-flex justify-content-between align-items-center small mb-2 gap-2">
                      <span className="text-muted flex-shrink-0">Booking ID</span>
                      <span className="fw-bold">#{displayBookingId}</span>
                    </div>
                  )}
                  {displayPaymentId && (
                    <div className="d-flex justify-content-between align-items-center small mb-2 gap-2">
                      <span className="text-muted flex-shrink-0">Payment ID</span>
                      <span className="fw-bold">#{displayPaymentId}</span>
                    </div>
                  )}
                  {displayTransactionId && (
                    <div className="small mb-0">
                      <span className="text-muted d-block mb-1">Transaction ID</span>
                      <code
                        className="d-block fw-semibold text-dark p-2 rounded-2"
                        style={{
                          fontSize: 11,
                          wordBreak: 'break-all',
                          overflowWrap: 'anywhere',
                          background: '#EEF2FF',
                          lineHeight: 1.4,
                        }}
                        title={displayTransactionId}
                      >
                        {displayTransactionId}
                      </code>
                    </div>
                  )}
                </div>
              )}

              <div className="d-flex justify-content-between mb-2 small gap-2">
                <span className="text-muted">Consultation Fee</span>
                <span className="fw-semibold flex-shrink-0">${consultationFee.toFixed(2)}</span>
              </div>
              <div className="d-flex justify-content-between mb-2 small gap-2">
                <span className="text-muted">
                  Platform Fee{' '}
                  <i className="bi bi-info-circle" title="Non-refundable processing fee" style={{ fontSize: 11 }} />
                </span>
                <span className="fw-semibold flex-shrink-0">${platformFee.toFixed(2)}</span>
              </div>
              <div className="d-flex justify-content-between align-items-center mb-2 small gap-2">
                <span className="text-muted flex-shrink-0">Status</span>
                <span className={`badge rounded-pill px-2 px-sm-3 py-1 ${paymentStatusBadge(paymentStatus)}`}>
                  {paymentStatus}
                </span>
              </div>
              {isHistoryView && booking?.status && (
                <div className="d-flex justify-content-between align-items-center mb-2 small gap-2">
                  <span className="text-muted flex-shrink-0">Booking Status</span>
                  <span className="badge rounded-pill bg-light text-dark border px-2 px-sm-3 py-1">
                    {booking.status}
                  </span>
                </div>
              )}

              <hr className="my-3" />

              <div className="d-flex justify-content-between align-items-center mb-3 gap-2">
                <span className="fw-bold">Total Amount</span>
                <span className="fw-bold fs-5 flex-shrink-0">${totalAmount.toFixed(2)}</span>
              </div>

              <div
                className="rounded-3 p-3 mb-3 mb-md-4 d-flex align-items-center gap-2"
                style={{
                  background:
                    paymentStatus === 'SUCCEEDED'
                      ? '#ECFDF5'
                      : paymentStatus === 'REFUNDED'
                        ? '#EFF6FF'
                        : '#FEF3C7',
                  border: `1px solid ${
                    paymentStatus === 'SUCCEEDED'
                      ? '#A7F3D0'
                      : paymentStatus === 'REFUNDED'
                        ? '#BFDBFE'
                        : '#FCD34D'
                  }`,
                  color:
                    paymentStatus === 'SUCCEEDED'
                      ? '#047857'
                      : paymentStatus === 'REFUNDED'
                        ? '#1D4ED8'
                        : '#92400E',
                }}
              >
                <i
                  className={`bi flex-shrink-0 ${
                    paymentStatus === 'SUCCEEDED'
                      ? 'bi-check-circle-fill'
                      : paymentStatus === 'REFUNDED'
                        ? 'bi-arrow-counterclockwise'
                        : 'bi-tag-fill'
                  }`}
                />
                <span className="small fw-semibold">
                  {paymentStatus === 'SUCCEEDED'
                    ? `Paid $${totalAmount.toFixed(2)}`
                    : paymentStatus === 'REFUNDED'
                      ? `Refunded $${totalAmount.toFixed(2)}`
                      : paymentStatus === 'Pending Payment'
                        ? `You will be paying $${totalAmount.toFixed(2)}`
                        : `Payment ${paymentStatus} · $${totalAmount.toFixed(2)}`}
                </span>
              </div>

              {!isHistoryView && (
                <>
                  <p className="fw-bold small mb-2">Payment Method</p>
                  <div
                    className="d-flex align-items-center gap-2 p-3 rounded-3 border mb-2"
                    style={{ borderColor: '#BFDBFE', background: '#F8FAFC' }}
                  >
                    <input type="radio" checked readOnly className="form-check-input m-0 flex-shrink-0" />
                    <i className="bi bi-credit-card-2-front text-primary flex-shrink-0" />
                    <div className="flex-grow-1 min-w-0">
                      <p className="mb-0 fw-semibold small">Credit / Debit Card</p>
                      <small className="text-muted" style={{ fontSize: 11 }}>Visa, Mastercard & more</small>
                    </div>
                  </div>
                  <p className="text-muted small mb-3" style={{ fontSize: 12 }}>
                    <i className="bi bi-lock-fill me-1" />
                    Your payment information is secure and encrypted.
                  </p>

                  <button
                    type="button"
                    className="btn btn-primary w-100 py-2 rounded-3 fw-bold"
                    onClick={handleProceedToPayment}
                  >
                    <i className="bi bi-lock-fill me-2" />
                    Proceed to Payment
                  </button>
                  <p className="text-center text-muted mt-3 mb-0" style={{ fontSize: 11 }}>
                    By proceeding, you agree to our{' '}
                    <a href="/user/policy" className="text-primary text-decoration-none">
                      Terms & Conditions
                    </a>
                  </p>
                </>
              )}

              {isHistoryView && (
                <button
                  type="button"
                  className="btn btn-outline-primary w-100 py-2 rounded-3 fw-bold"
                  onClick={() => navigate('/user/bookings')}
                >
                  <i className="bi bi-calendar-check me-2" />
                  Go to My Bookings
                </button>
              )}
            </div>
          </div>

          {/* Left column — single card for all booking details */}
          <div className="col-12 col-lg-8 order-2 order-lg-1">
            <div
              className="bg-white rounded-4 border p-3 p-md-4 h-100"
              style={{ borderColor: '#E5E7EB' }}
            >
              <SectionBlock icon="bi-calendar-event" title="Appointment Details" className="mb-3">
                <div className="row g-2 g-md-3">
                  <div className="col-6 col-sm-6 col-md-3">
                    <small className="text-muted d-block" style={{ fontSize: 12 }}>Date</small>
                    <p className="fw-semibold mb-0" style={{ fontSize: 14 }}>{formatSlotDate(slot?.date)}</p>
                  </div>
                  <div className="col-6 col-sm-6 col-md-3">
                    <small className="text-muted d-block" style={{ fontSize: 12 }}>Time</small>
                    <p className="fw-semibold mb-0" style={{ fontSize: 14 }}>
                      {formatTime(slot?.start_time)}
                      {durationLabel ? ` (${durationLabel})` : ''}
                    </p>
                  </div>
                  <div className="col-6 col-sm-6 col-md-3">
                    <small className="text-muted d-block" style={{ fontSize: 12 }}>Type</small>
                    <p className="fw-semibold mb-0" style={{ fontSize: 14 }}>{callTypeLabel}</p>
                  </div>
                  <div className="col-6 col-sm-6 col-md-3">
                    <small className="text-muted d-block" style={{ fontSize: 12 }}>Consultation For</small>
                    <p className="fw-semibold mb-0 text-break" style={{ fontSize: 14 }}>{patientLabel}</p>
                    {bookedByLabel && (
                      <small className="text-muted d-block mt-1" style={{ fontSize: 11 }}>
                        Booked by {bookedByLabel}
                      </small>
                    )}
                  </div>
                </div>
              </SectionBlock>

              <hr className="my-3" style={{ borderColor: '#E5E7EB', opacity: 1 }} />

              <SectionBlock icon="bi-person" title="Doctor Details" className="mb-3">
                <div className="d-flex flex-column flex-md-row gap-3 gap-md-4">
                  <div className="d-flex gap-3 flex-grow-1 min-w-0">
                    <img
                      src={getMediaUrl(doctor?.image) || 'https://via.placeholder.com/80?text=Dr'}
                      alt={doctor?.name || 'Doctor'}
                      className="rounded-circle flex-shrink-0"
                      style={{ width: 56, height: 56, objectFit: 'cover' }}
                    />
                    <div className="min-w-0">
                      <h6 className="fw-bold mb-1 d-flex align-items-center gap-1 flex-wrap">
                        <span className="text-break">
                          {doctor?.name?.startsWith('Dr') ? doctor.name : `Dr. ${doctor?.name || '—'}`}
                        </span>
                        <i className="bi bi-patch-check-fill text-primary flex-shrink-0" style={{ fontSize: 14 }} />
                      </h6>
                      <p className="text-muted small mb-1">{doctor?.qualification || '—'}</p>
                      <p className="fw-semibold small mb-1 text-dark">{doctor?.specialization || '—'}</p>
                      <p className="text-muted small mb-0">
                        {doctor?.experience != null ? `${doctor.experience}+ Years of Experience` : 'Experience N/A'}
                      </p>
                    </div>
                  </div>
                  <div className="ps-0 ps-md-4 border-0 border-md-start w-100" style={{ maxWidth: 280 }}>
                    <div className="d-flex gap-2">
                      <i className="bi bi-hospital text-primary mt-1 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="fw-semibold mb-1 small">Fitnova Healthcare</p>
                        <p className="text-muted mb-2 text-break" style={{ fontSize: 12, lineHeight: 1.4 }}>
                          {doctor?.address || 'Online consultation available'}
                        </p>
                        {doctor?.address && (
                          <a
                            className="small text-primary text-decoration-none fw-semibold"
                            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(doctor.address)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <i className="bi bi-geo-alt me-1" />
                            View on Map
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </SectionBlock>

              <hr className="my-3" style={{ borderColor: '#E5E7EB', opacity: 1 }} />

              <SectionBlock
                icon="bi-clipboard2-pulse"
                title="Reason for Visit"
                onEdit={isHistoryView ? undefined : () => setEditingReason((v) => !v)}
                editLabel={editingReason ? 'Done' : 'Edit'}
                className="mb-3"
              >
                {editingReason && !isHistoryView ? (
                  <>
                    <textarea
                      className="form-control"
                      rows={3}
                      placeholder="Describe your symptoms or reason for visit..."
                      value={reasonNotes}
                      onChange={(e) => setReasonNotes(e.target.value)}
                      maxLength={500}
                      style={{
                        resize: 'none',
                        borderColor: '#E5E7EB',
                        fontSize: 14,
                        color: '#1F2937',
                      }}
                    />
                    <div className="d-flex justify-content-end mt-1">
                      <small className="text-muted" style={{ fontSize: 11 }}>
                        {reasonNotes.length}/500
                      </small>
                    </div>
                  </>
                ) : (
                  <p className="mb-0 text-dark text-break" style={{ fontSize: 14 }}>
                    {displayReason?.trim() || 'No reason / symptoms added'}
                  </p>
                )}
              </SectionBlock>

              <hr className="my-3" style={{ borderColor: '#E5E7EB', opacity: 1 }} />

              <SectionBlock
                icon="bi-folder2-open"
                title="Patients Reports"
                onEdit={isHistoryView ? undefined : () => setEditingReport((v) => !v)}
                editLabel={editingReport ? 'Done' : 'Edit'}
              >
                <input
                  ref={reportInputRef}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/*"
                  className="d-none"
                  multiple
                  onChange={handleReportFileChange}
                />
                {medicalReportFiles.length > 0 ? (
                  <div className="d-flex flex-column gap-2">
                    {medicalReportFiles.map((file, index) => (
                      <div key={`${file.name || 'report'}-${index}`} className="d-flex flex-column flex-sm-row gap-2 align-items-stretch">
                        <FileCard
                          name={file.name || `Medical Report ${index + 1}`}
                          subtitle={`Uploaded on ${new Date().toLocaleDateString('en-GB', {
                            day: 'numeric', month: 'short', year: 'numeric',
                          })}`}
                          onView={() => openReport(file)}
                        />
                        {editingReport && !isHistoryView && (
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm rounded-3 fw-semibold flex-shrink-0 align-self-sm-center"
                            onClick={() =>
                              setMedicalReportFiles((prev) => prev.filter((_, i) => i !== index))
                            }
                          >
                            <i className="bi bi-trash me-1" />
                            Remove
                          </button>
                        )}
                      </div>
                    ))}
                    {editingReport && !isHistoryView && medicalReportFiles.length < MAX_REPORT_FILES && (
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm rounded-3 fw-semibold align-self-start"
                        onClick={() => reportInputRef.current?.click()}
                      >
                        <i className="bi bi-plus-lg me-1" />
                        Add more ({medicalReportFiles.length}/{MAX_REPORT_FILES})
                      </button>
                    )}
                  </div>
                ) : editingReport && !isHistoryView ? (
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm rounded-3 fw-semibold"
                    onClick={() => reportInputRef.current?.click()}
                  >
                    <i className="bi bi-upload me-1" />
                    Upload Reports
                  </button>
                ) : (
                  <p className="mb-0 text-muted small">No medical reports attached</p>
                )}
              </SectionBlock>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @media (min-width: 992px) {
          .booking-summary-sidebar {
            position: sticky;
            top: 88px;
          }
        }
        @media (max-width: 575.98px) {
          .booking-summary-sidebar .badge {
            font-size: 0.7rem;
          }
        }
      `}</style>
    </div>
  );
}

export default BookingSummary;
